import type { AnalyseOptions, EngineInfo, EnginePosition, EngineUpdate } from './types.ts';

/**
 * A chess engine. One engine serves every plugin that asks: a new analysis
 * supersedes the one running, so a caller that wants an answer for one
 * position never waits behind a whole-game scan. The scan is not here; it
 * is `analysis`, built on top of this.
 *
 * Provided under the name `engine`; see `ENGINE_API`.
 */
export interface Engine {
  /** Who the engine is. Resolves once the engine has started. */
  info(): Promise<EngineInfo>;

  /**
   * Search a position, streaming updates as the depth grows. The iteration
   * ends when the search stops: the depth or time was reached, `stop` was
   * called, the signal aborted, or another analysis took the engine.
   */
  analyse(position: EnginePosition, options?: AnalyseOptions): AsyncIterable<EngineUpdate>;

  /** Search a position and return the last update. The same as draining `analyse`. */
  evaluate(position: EnginePosition, options?: AnalyseOptions): Promise<EngineUpdate>;

  /** Stop whatever search is running. Resolves once the engine has stopped. */
  stop(): Promise<void>;

  /** Release the engine. The instance cannot be used afterwards. */
  dispose(): Promise<void>;
}

/** The name the engine is registered under. */
export const ENGINE_API = 'engine';

/** The version of this interface, for `provides` and `requires`. */
export const ENGINE_API_VERSION = '1.0.0';
